/**
 * "Needs you" for the Roost exceptions dashboard (WI 34293): items only Tim can
 * move, each a real link that opens the item. Pure; no Angular, no I/O.
 *
 * Sources (the choice the ticket asks to write down):
 *  1. Bridge review tasks (GET ryn-db/review-tasks). `needs-human` = the
 *     cross-seat reviewer says a human step or decision is required; `green`
 *     inside the last 72h = reviewed and waiting on Tim's vote/complete (Tim
 *     reviews Ryn PRs once the labels are on). Link: the ADO PR.
 *  2. Seat open inbounds parked on Tim (work-queue openInbounds whose
 *     parkedReason names Tim). Link: the Teams chat.
 * Not sourced today (gaps on the ticket): ryn-ask-tim questions (sent as Teams
 * DMs, never stored). Bug 34839: a review task outlives its PR, so rows whose PR
 * is closed are dropped against `openPrs` (GET ryn-db/active-prs); an unknown
 * repo keeps every row (see open-prs.logic.ts).
 */
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { WallboardSeat } from './wallboard.logic';
import { formatDuration } from './wallboard.logic';
import { OpenPrIndex, isPrClosed } from './open-prs.logic';

export const GREEN_REVIEW_WINDOW_MS = 72 * 60 * 60_000;
export const NEEDS_HUMAN_WINDOW_MS = 14 * 24 * 60 * 60_000;
export const ADO_ORG = 'https://dev.azure.com/universalplantdev';
export const ADO_PROJECT = 'UPS1_Workspace';

/** One row of GET /api/ryn-db/review-tasks (ReviewTaskEndpoints.Rows.cs). */
export interface ReviewTaskRow {
  reviewTaskId: number;
  repo: string;
  prId: number;
  authorInstanceId: string | null;
  builderInstanceId: string | null;
  status: string;
  reviewerInstanceId: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

export type NeedsYouTag = 'DECIDE' | 'ASK' | 'PR';

export interface NeedsYouItem {
  key: string;
  tag: NeedsYouTag;
  title: string;
  meta: string;
  href: string;
  atMs: number;
}

export function prHref(repo: string, prId: number): string {
  const name =
    (repo || ADO_PROJECT).split('/').filter(Boolean).pop() ?? ADO_PROJECT;
  return `${ADO_ORG}/${ADO_PROJECT}/_git/${encodeURIComponent(name)}/pullrequest/${prId}`;
}

/** Teams deep link for a chat thread id ("19:...@thread.v2" / "@unq.gbl.spaces"). */
export function teamsChatHref(chatId: string | null): string | null {
  if (!chatId || !chatId.startsWith('19:')) return null;
  return `https://teams.microsoft.com/l/chat/${encodeURIComponent(chatId)}/0`;
}

const TAG_RANK: Record<NeedsYouTag, number> = { DECIDE: 0, ASK: 1, PR: 2 };
const NAMES_TIM = /\btim\b/i;

export function buildNeedsYou(
  reviewTasks: ReviewTaskRow[],
  seats: WallboardSeat[],
  nowMs: number,
  openPrs: OpenPrIndex | null = null
): NeedsYouItem[] {
  const out: NeedsYouItem[] = [];
  const seenPr = new Set<number>();
  for (const t of reviewTasks) {
    const status = (t.status ?? '').toLowerCase();
    const at = parseUtcMs(t.completedAt) ?? parseUtcMs(t.createdAt);
    if (at === null || seenPr.has(t.prId)) continue;
    if (isPrClosed(openPrs, t.repo, t.prId)) continue;
    const who = t.reviewerInstanceId ? ` by ${t.reviewerInstanceId}` : '';
    const ago = `${formatDuration(nowMs - at)} ago`;
    if (status === 'needs-human' && nowMs - at <= NEEDS_HUMAN_WINDOW_MS) {
      seenPr.add(t.prId);
      out.push({
        key: `pr|${t.prId}`,
        tag: 'DECIDE',
        title: `PR ${t.prId} needs a human step`,
        meta: `Reviewed${who} ${ago} · not a code defect, blocks the merge`,
        href: prHref(t.repo, t.prId),
        atMs: at,
      });
    } else if (status === 'green' && nowMs - at <= GREEN_REVIEW_WINDOW_MS) {
      seenPr.add(t.prId);
      out.push({
        key: `pr|${t.prId}`,
        tag: 'PR',
        title: `PR ${t.prId} review green, waiting on your vote`,
        meta: `Reviewed${who} ${ago}${t.builderInstanceId ? ` · built by ${t.builderInstanceId}` : ''}`,
        href: prHref(t.repo, t.prId),
        atMs: at,
      });
    }
  }
  for (const seat of seats) {
    if (seat.retired) continue;
    for (const oi of seat.openInbounds ?? []) {
      if (!oi.parkedReason || !NAMES_TIM.test(oi.parkedReason)) continue;
      const href = teamsChatHref(oi.chatId);
      if (!href) continue;
      const at = parseUtcMs(oi.receivedAt) ?? nowMs;
      out.push({
        key: `ask|${seat.instanceId}|${oi.chatId}`,
        tag: 'ASK',
        title: `${seat.instanceId} is waiting on you`,
        meta: oi.parkedReason,
        href,
        atMs: at,
      });
    }
  }
  return out.sort(
    (a, b) => TAG_RANK[a.tag] - TAG_RANK[b.tag] || b.atMs - a.atMs
  );
}
